import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useHttp } from "../hooks/useHttp";
import useDebounce from "../hooks/useDebounce";
import { API_URLS, DEFAULT_HEADERS } from "../util/constants";
import { handleApiError } from "../util/errorHandler";

export const ProductContext = createContext({});
export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const lastRequestHasVideoRef = useRef(null);

  const [productOrder, setProductOrder] = useState({
    type: "Alphabetic",
    order: "ASC",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [productFilters, setProductFilters] = useState([]);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [hasVideo, setHasVideo] = useState(false);
  const debouncedSearch = useDebounce(search);
  const { sendRequest } = useHttp();
  useEffect(() => setPage(1), [categories, debouncedSearch, productOrder, hasVideo]);

  const ProductQueryObj = useCallback(() => {
    return {
      page: page,
      filters: {
        category: categories,
        search: debouncedSearch,
        order: productOrder,
        has_video: hasVideo,
      },
    };
  }, [page, categories, debouncedSearch, productOrder, hasVideo]);

  useEffect(() => {
    const query = ProductQueryObj();
    const thisRequestHasVideo = query.filters.has_video;
    lastRequestHasVideoRef.current = thisRequestHasVideo;

    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const responseData = await sendRequest(
          API_URLS.PRODUCTS,
          "POST",
          JSON.stringify(query),
          DEFAULT_HEADERS.JSON
        );
        if (responseData.success) {
          // Só aplica se a resposta for do mesmo filtro pedido (evita race: resposta antiga sobrescrever)
          if (lastRequestHasVideoRef.current === thisRequestHasVideo) {
            setProducts(responseData);
          }
        } else {
          throw new Error(responseData.message);
        }
      } catch (error) {
        if (lastRequestHasVideoRef.current === thisRequestHasVideo) {
          handleApiError(error, { context: "ProductContext.loadProducts", showToast: false });
        }
      } finally {
        if (lastRequestHasVideoRef.current === thisRequestHasVideo) {
          setIsLoading(false);
        }
      }
    };
    loadProducts();
  }, [ProductQueryObj, sendRequest]);

  useEffect(() => {
    const loadFilters = async () => {
      setIsLoadingFilters(true);
      try {
        const responseData = await sendRequest(
          API_URLS.PRODUCTS_CATEGORY,
          "GET",
          null,
          DEFAULT_HEADERS.JSON
        );
        if (responseData.success) {
          setProductFilters(responseData.categories);
        } else {
          throw new Error(responseData.message);
        }
      } catch (error) {
        handleApiError(error, { context: "ProductContext.loadFilters", showToast: false });
      } finally {
        setIsLoadingFilters(false);
      }
    };
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProductContext.Provider
      value={{
        products: products.products,
        productQuery: {
          total_pages: products.total_pages,
          actual_page: products.actual_page,
          total_products: products.total_products,
        },
        productOrder: productOrder,
        isLoading,
        isLoadingFilters,
        productFilters,
        page,
        categories,
        search,
        setPage,
        setCategories,
        setSearch,
        setProductOrder,
        hasVideo,
        setHasVideo,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
